// ============================================================
// AboutProDev.jsx
//
// About page for the ProDevConsult platform. Contains:
//   - Full-height hero section with background image,
//     glassmorphism overlay, headline, and CTA button
//   - Three content cards explaining what the platform offers,
//     why to choose it, and what makes it different
//
// Clean Code:
//   - BRAND colors centralized
//   - Card data extracted to ABOUT_CARDS constant
//   - No repeated JSX — cards rendered via .map()
// ============================================================

import React from "react";
import { styled } from "@mui/material/styles";
import {
    Typography, Button, Container,
    Card, CardContent, Box, Grid, Chip, Divider,
} from "@mui/material";
import { Fade } from "react-awesome-reveal";
import { Link } from "react-router-dom";

// ── MUI Icons ────────────────────────────────────────────────
import EditNoteIcon from "@mui/icons-material/EditNote";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CodeIcon from "@mui/icons-material/Code";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BuildIcon from "@mui/icons-material/Build";
import DevicesIcon from "@mui/icons-material/Devices";
import TerminalIcon from "@mui/icons-material/Terminal";
import BugReportIcon from "@mui/icons-material/BugReport";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:  "#100720",
    navy:  "#09122C",
    gold:  "#E5C95F",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.7)",
};

/** Hero background image */
const HERO_BG = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/**
 * ABOUT_CARDS
 * The three content cards shown below the hero.
 * Each card has an icon, title, and list of bullet points.
 *
 * To add or update a card, edit this array only —
 * no JSX changes needed.
 */
const ABOUT_CARDS = [
    {
        icon:  <SchoolIcon sx={{ fontSize: 32, color: BRAND.gold }} />,
        title: "One-on-One Training & Mentorship",
        intro: "Become a full-stack developer with expert-led training across 16 professional course tracks.",
        points: [
            { icon: <CodeIcon fontSize="small" />,    text: "Java, Python, C++, C#, TypeScript, JavaScript" },
            { icon: <DevicesIcon fontSize="small" />, text: "React, Angular, HTML5 & CSS, Spring Boot, Express.js" },
            { icon: <BuildIcon fontSize="small" />,   text: "MongoDB, MySQL, Postgres, GitHub, Linux, IoT" },
            { icon: <BugReportIcon fontSize="small"/>, text: "Software Testing, DSA training & certification" },
        ],
    },
    {
        icon:  <EmojiEventsIcon sx={{ fontSize: 32, color: BRAND.gold }} />,
        title: "Why Choose ProDev?",
        intro: "We combine structured learning with real tools and real outcomes.",
        points: [
            { icon: <CheckCircleIcon fontSize="small" />, text: "Learn from industry professionals with real-world experience" },
            { icon: <CheckCircleIcon fontSize="small" />, text: "Build production projects and deploy them live (Premium)" },
            { icon: <CheckCircleIcon fontSize="small" />, text: "Master full-stack development from beginner to advanced" },
            { icon: <CheckCircleIcon fontSize="small" />, text: "Flexible learning — study at your own pace with goal setting" },
            { icon: <CheckCircleIcon fontSize="small" />, text: "Online exams and professional certification on all plans" },
        ],
    },
    {
        icon:  <AutoAwesomeIcon sx={{ fontSize: 32, color: BRAND.gold }} />,
        title: "Why We Are Different",
        intro: "Features built for serious developers — not just passive video watching.",
        points: [
            { icon: <TerminalIcon fontSize="small" />,    text: "Inbuilt online compiler — no local setup needed" },
            { icon: <CodeIcon fontSize="small" />,        text: "Practice-ready code snippets for every course" },
            { icon: <BugReportIcon fontSize="small" />,   text: "Assignments with detailed solutions" },
            { icon: <BuildIcon fontSize="small" />,       text: "Extensive DSA training to build your coding muscle" },
            { icon: <RocketLaunchIcon fontSize="small"/>, text: "Production project development & live deployment (Premium)" },
        ],
    },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * HeroSection
 * Full-viewport hero with dark-overlay background image.
 * Content is left-aligned on desktop, centered on mobile.
 */
const HeroSection = styled(Box)(({ theme }) => ({
    position: "relative",
    width: "100%",
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.75), rgba(16,7,32,0.75)), url(${HERO_BG})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    padding: "40px 24px",
    [theme.breakpoints.down("sm")]: {
        justifyContent: "center",
        textAlign: "center",
    },
}));

/**
 * GlassCard
 * Glassmorphism overlay containing the hero text and CTA.
 */
const GlassCard = styled(Box)(({ theme }) => ({
    color: BRAND.white,
    maxWidth: "660px",
    textAlign: "left",
    backdropFilter: "blur(8px)",
    padding: "36px 32px",
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.15)",
    [theme.breakpoints.down("sm")]: {
        textAlign: "center",
        padding: "24px 20px",
    },
}));

/**
 * StyledCard
 * Dark navy content card used for the three about sections.
 * Lifts on hover.
 */
const StyledCard = styled(Card)({
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "16px",
    padding: "8px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
    border: "1px solid rgba(255,255,255,0.07)",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
        transform: "translateY(-5px)",
        boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
    },
});

/**
 * GoldHighlight
 * Inline gold text for emphasizing key phrases.
 */
const GoldHighlight = styled("span")({
    fontWeight: 700,
    color: BRAND.gold,
});

/**
 * ActionButton
 * Gold pill CTA button used in the hero section.
 */
const ActionButton = styled(Button)({
    marginTop: "28px",
    fontSize: "1rem",
    fontWeight: 700,
    padding: "13px 36px",
    backgroundColor: BRAND.gold,
    color: BRAND.dark,
    borderRadius: "50px",
    textTransform: "none",
    transition: "transform 0.2s ease, background-color 0.2s ease",
    "&:hover": {
        backgroundColor: "#f0d060",
        transform: "translateY(-2px)",
        boxShadow: "0 8px 24px rgba(229,201,95,0.3)",
    },
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function AboutProDev() {
    return (
        <Box sx={{ backgroundColor: BRAND.white, fontFamily: "'Poppins', sans-serif" }}>

            {/* ── HERO SECTION ── */}
            <HeroSection>
                <GlassCard>
                    <Fade direction="down" triggerOnce>

                        {/* Platform label chip */}
                        <Chip
                            label="Software Training & Consultancy"
                            size="small"
                            sx={{
                                backgroundColor: "rgba(229,201,95,0.15)",
                                color: BRAND.gold,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                border: "1px solid rgba(229,201,95,0.3)",
                                mb: 2,
                                letterSpacing: "0.5px",
                            }}
                        />

                        {/* Main headline */}
                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: "2rem", md: "3rem" },
                                color: BRAND.gold,
                                fontFamily: "'Georgia', serif",
                                lineHeight: 1.15,
                                mb: 1,
                            }}
                        >
                            ProDevConsult
                        </Typography>

                    </Fade>

                    <Fade direction="up" delay={300} triggerOnce>

                        {/* Subtitle */}
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: "0.95rem", md: "1.1rem" },
                                color: BRAND.muted,
                                lineHeight: 1.8,
                                mt: 1,
                            }}
                        >
                            ProDevConsult delivers{" "}
                            <GoldHighlight>premium software development</GoldHighlight> and{" "}
                            <GoldHighlight>full-stack training</GoldHighlight> across 16 course tracks
                            including React, Node.js, Spring Boot, Java, Python, and more.
                            Our expert instructors bring{" "}
                            <GoldHighlight>industry-standard best practices</GoldHighlight> to
                            every learner.
                        </Typography>

                        {/* Quick feature chips */}
                        <Box display="flex" flexWrap="wrap" gap={1} mt={2.5}>
                            {[
                                "16 Course Tracks",
                                "Inbuilt Compiler",
                                "DSA Training",
                                "Certification",
                            ].map((label) => (
                                <Chip
                                    key={label}
                                    label={label}
                                    size="small"
                                    sx={{
                                        backgroundColor: "rgba(255,255,255,0.1)",
                                        color: BRAND.white,
                                        fontWeight: 600,
                                        fontSize: "0.72rem",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                    }}
                                />
                            ))}
                        </Box>

                    </Fade>

                    <Fade direction="up" delay={500} triggerOnce>
                        <Link to="/plan-application-page" style={{ textDecoration: "none" }}>
                            <ActionButton startIcon={<EditNoteIcon />}>
                                Start Learning
                            </ActionButton>
                        </Link>
                    </Fade>
                </GlassCard>
            </HeroSection>

            {/* ── CONTENT CARDS ── */}
            <Box sx={{ backgroundColor: BRAND.dark, py: 8 }}>
                <Container maxWidth="md">

                    {/* Section label */}
                    <Fade direction="down" triggerOnce>
                        <Typography
                            variant="overline"
                            display="block"
                            textAlign="center"
                            sx={{
                                color: BRAND.gold,
                                fontWeight: 800,
                                letterSpacing: "3px",
                                fontSize: "0.8rem",
                                mb: 1,
                            }}
                        >
                            ABOUT THE PLATFORM
                        </Typography>
                        <Typography
                            variant="h4"
                            textAlign="center"
                            fontWeight={900}
                            sx={{
                                color: BRAND.white,
                                fontFamily: "'Georgia', serif",
                                mb: 5,
                                fontSize: { xs: "1.6rem", md: "2rem" },
                            }}
                        >
                            Everything You Need to{" "}
                            <Box component="span" sx={{ color: BRAND.gold }}>
                                Get Hired
                            </Box>
                        </Typography>
                    </Fade>

                    {/* Cards rendered from ABOUT_CARDS data */}
                    {ABOUT_CARDS.map((card, index) => (
                        <Fade
                            key={card.title}
                            direction="up"
                            delay={index * 150}
                            triggerOnce
                        >
                            <StyledCard sx={{ mb: 3 }}>
                                <CardContent sx={{ p: 3 }}>

                                    {/* Card header */}
                                    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                        {card.icon}
                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                            sx={{
                                                color: BRAND.white,
                                                fontFamily: "'Georgia', serif",
                                            }}
                                        >
                                            {card.title}
                                        </Typography>
                                    </Box>

                                    {/* Card intro */}
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "rgba(255,255,255,0.6)", mb: 2, lineHeight: 1.7 }}
                                    >
                                        {card.intro}
                                    </Typography>

                                    <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 2 }} />

                                    {/* Bullet points */}
                                    <Box display="flex" flexDirection="column" gap={1.2}>
                                        {card.points.map((point, i) => (
                                            <Box
                                                key={i}
                                                display="flex"
                                                alignItems="center"
                                                gap={1.5}
                                            >
                                                <Box sx={{ color: BRAND.gold, display: "flex", flexShrink: 0 }}>
                                                    {point.icon}
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}
                                                >
                                                    {point.text}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                </CardContent>
                            </StyledCard>
                        </Fade>
                    ))}

                </Container>
            </Box>

        </Box>
    );
}
